import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { armazenamento } from './armazenamento.ts';

/**
 * Estado do percurso de lição (H-050).
 *
 * Persistido localmente para cumprir o critério "abandono a meio, volto, e
 * retomo no exercício onde estava". Ainda **não** é a fila de sincronização de
 * RNF-013 nem o registo de eventos de pontos de RF-040 — não há servidor. Quando
 * houver, este estado passa a ser a cópia local e a origem da verdade muda.
 */

export interface LessonState {
  index: number;
  pontos: number;
  /** Exercícios acertados sem qualquer tentativa falhada. */
  firstTry: string[];
  /** Exercícios que já pagaram pontos. Impede que repetir um exercício o volte a pagar. */
  awarded: string[];
  /**
   * Exercícios resolvidos depois de ver a solução (RN-007). Não contam para o
   * certificado até serem refeitos sem ajuda — daí serem guardados e não
   * apenas contados.
   */
  comAjuda: string[];
  /** Rascunho de código por exercício, para não perder trabalho ao navegar. */
  drafts: Record<string, string>;
  completedAt?: number;
}

interface Store {
  lessons: Record<string, LessonState>;
  get(lessonId: string): LessonState;
  setIndex(lessonId: string, index: number): void;
  setDraft(lessonId: string, exerciseId: string, code: string): void;
  award(lessonId: string, exerciseId: string, pontos: number, firstTry: boolean): void;
  marcarComAjuda(lessonId: string, exerciseId: string): void;
  /** RN-007 — refeito sem a solução à vista: volta a contar para o certificado. */
  desmarcarComAjuda(lessonId: string, exerciseId: string): void;
  /**
   * RN-007 — reabre uma lição concluída para a refazer. O `completedAt` fica
   * (a lição continua concluída para o progresso); o índice volta ao
   * princípio e os rascunhos dos exercícios resolvidos com ajuda são
   * apagados — refazer «sem ajuda» com a solução revelada ainda no editor
   * não é refazer.
   */
  reabrirParaRefazer(lessonId: string): void;
  complete(lessonId: string): void;
  reset(lessonId: string): void;
}

const EMPTY: LessonState = { index: 0, pontos: 0, firstTry: [], awarded: [], comAjuda: [], drafts: {} };

export const useLessonProgress = create<Store>()(
  persist(
    (set, get) => ({
      lessons: {},

      get(lessonId) {
        return get().lessons[lessonId] ?? EMPTY;
      },

      setIndex(lessonId, index) {
        set((state) => ({
          lessons: { ...state.lessons, [lessonId]: { ...(state.lessons[lessonId] ?? EMPTY), index } },
        }));
      },

      setDraft(lessonId, exerciseId, code) {
        set((state) => {
          const current = state.lessons[lessonId] ?? EMPTY;
          return {
            lessons: {
              ...state.lessons,
              [lessonId]: { ...current, drafts: { ...current.drafts, [exerciseId]: code } },
            },
          };
        });
      },

      award(lessonId, exerciseId, pontos, firstTry) {
        set((state) => {
          const current = state.lessons[lessonId] ?? EMPTY;
          // Os pontos de um exercício é atribuído uma só vez: repetir um exercício
          // já concluído não o volta a pagar (precursor de RNF-014).
          const awarded = current.awarded ?? [];
          if (awarded.includes(exerciseId)) return state;
          return {
            lessons: {
              ...state.lessons,
              [lessonId]: {
                ...current,
                // `?? 0` porque este campo já se chamou `xp`: um estado
                // guardado antes da renomeação não tem `pontos`, e sem a
                // salvaguarda a soma dava NaN em silêncio. O estado local não
                // tem versão de esquema — é a fragilidade que o `docs/estado.md`
                // já registava, e esta renomeação foi a primeira a tropeçar nela.
                pontos: (current.pontos ?? 0) + pontos,
                awarded: [...awarded, exerciseId],
                firstTry: firstTry ? [...current.firstTry, exerciseId] : current.firstTry,
              },
            },
          };
        });
      },

      marcarComAjuda(lessonId, exerciseId) {
        set((state) => {
          const current = state.lessons[lessonId] ?? EMPTY;
          const jaLa = current.comAjuda ?? [];
          if (jaLa.includes(exerciseId)) return state;
          return {
            lessons: {
              ...state.lessons,
              [lessonId]: { ...current, comAjuda: [...jaLa, exerciseId] },
            },
          };
        });
      },

      desmarcarComAjuda(lessonId, exerciseId) {
        set((state) => {
          const current = state.lessons[lessonId] ?? EMPTY;
          const jaLa = current.comAjuda ?? [];
          if (!jaLa.includes(exerciseId)) return state;
          return {
            lessons: {
              ...state.lessons,
              [lessonId]: { ...current, comAjuda: jaLa.filter((id) => id !== exerciseId) },
            },
          };
        });
      },

      reabrirParaRefazer(lessonId) {
        set((state) => {
          const current = state.lessons[lessonId] ?? EMPTY;
          const comAjuda = new Set(current.comAjuda ?? []);
          const drafts = Object.fromEntries(
            Object.entries(current.drafts ?? {}).filter(([id]) => !comAjuda.has(id)),
          );
          return { lessons: { ...state.lessons, [lessonId]: { ...current, index: 0, drafts } } };
        });
      },

      complete(lessonId) {
        set((state) => ({
          lessons: {
            ...state.lessons,
            [lessonId]: { ...(state.lessons[lessonId] ?? EMPTY), completedAt: Date.now() },
          },
        }));
      },

      reset(lessonId) {
        set((state) => {
          const next = { ...state.lessons };
          delete next[lessonId];
          return { lessons: next };
        });
      },
    }),
    { name: 'progresso-licoes', storage: armazenamento() },
  ),
);
